import { createElement } from "react";

import { HourglassOutlined, ProfileOutlined, ThunderboltOutlined } from "@ant-design/icons";
import { clsx } from "clsx";

import { RVIcon, RVIconSizeOptions, type RVIconSizes, RVIconVariantOptions } from "@regal-voice/ui";
import sharedStyles from "Components/elements/RVFlow/Shared/FlowNodeBlocks/FlowNodeBlocks.module.scss";
import { NodeType } from "Types/Journey";

type RvIconConfig = {
    icon: string;
    size?: RVIconSizes;
    variant?: (typeof RVIconVariantOptions)[keyof typeof RVIconVariantOptions];
    color?: string;
};

type NodeIconDefinition = { kind: "ant"; element: JSX.Element } | { kind: "rv"; config: RvIconConfig };

const RV_ICON_DEFAULTS = {
    variant: RVIconVariantOptions.CIRCLE,
    size: RVIconSizeOptions.DEFAULT,
} as const;

const NODE_TYPE_ICONS: Record<NodeType, NodeIconDefinition> = {
    [NodeType.Trigger]: {
        kind: "ant",
        element: createElement(ThunderboltOutlined, { className: clsx(sharedStyles.antIcon) }),
    },
    [NodeType.Delay]: {
        kind: "ant",
        element: createElement(HourglassOutlined, { className: clsx(sharedStyles.antIcon) }),
    },
    [NodeType.CustomTask]: {
        kind: "ant",
        element: createElement(ProfileOutlined, { className: clsx(sharedStyles.antIcon) }),
    },
    [NodeType.Call]: { kind: "rv", config: { icon: "place-call", size: RVIconSizeOptions.SMALL } },
    [NodeType.SMS]: { kind: "rv", config: { icon: "outbound-message" } },
    [NodeType.AgentSMSTask]: { kind: "rv", config: { icon: "outbound-message" } },
    [NodeType.CancelTask]: { kind: "rv", config: { icon: "cancel" } },
    [NodeType.Conditional]: {
        kind: "rv",
        config: { icon: "conditional", variant: RVIconVariantOptions.DEFAULT },
    },
    [NodeType.ABTest]: {
        kind: "rv",
        config: { icon: "percentage", variant: RVIconVariantOptions.DEFAULT, size: RVIconSizeOptions.SMALL },
    },
    [NodeType.Webhook]: { kind: "rv", config: { icon: "webhook" } },
    [NodeType.AssignTargetAgent]: { kind: "rv", config: { icon: "headset" } },
    [NodeType.UpdateTask]: { kind: "rv", config: { icon: "sync" } },
    [NodeType.UpdateContact]: {
        kind: "rv",
        config: { icon: "silhouette", variant: RVIconVariantOptions.DEFAULT },
    },
    [NodeType.Reminder]: { kind: "rv", config: { icon: "reminder" } },
    [NodeType.ScheduledCallback]: { kind: "rv", config: { icon: "calendar" } },
    [NodeType.DecisionAI]: { kind: "rv", config: { icon: "split-logic", size: RVIconSizeOptions.SMALL } },
    [NodeType.DEMO_VoiceAI]: {
        kind: "rv",
        config: {
            icon: "task-call-outgoing-new",
            variant: RVIconVariantOptions.DEFAULT,
            size: RVIconSizeOptions.LARGE,
        },
    },
    [NodeType.DEMO_SMSAI]: { kind: "rv", config: { icon: "send-sms" } },
    [NodeType.DEMO_CustomTaskAI]: { kind: "rv", config: { icon: "profile-outlined" } },
    [NodeType.DEMO_SendEmail]: { kind: "rv", config: { icon: "email-outbound" } },
    [NodeType.DEMO_EmailTask]: { kind: "rv", config: { icon: "email-outbound" } },
    [NodeType.DEMO_RinglessVM]: { kind: "rv", config: { icon: "voicemail" } },
    [NodeType.DEMO_AgentlessCall]: {
        kind: "rv",
        config: {
            icon: "task-call-outgoing-new",
            variant: RVIconVariantOptions.DEFAULT,
            size: RVIconSizeOptions.LARGE,
        },
    },
    [NodeType.DEMO_EightByEight]: {
        kind: "rv",
        config: {
            icon: "task-call-outgoing-new",
            variant: RVIconVariantOptions.DEFAULT,
            size: RVIconSizeOptions.LARGE,
        },
    },
    [NodeType.DEMO_Genesys]: {
        kind: "rv",
        config: {
            icon: "task-call-outgoing-new",
            variant: RVIconVariantOptions.DEFAULT,
            size: RVIconSizeOptions.LARGE,
        },
    },
    [NodeType.DEMO_Talkdesk]: {
        kind: "rv",
        config: {
            icon: "task-call-outgoing-new",
            variant: RVIconVariantOptions.DEFAULT,
            size: RVIconSizeOptions.LARGE,
        },
    },
    [NodeType.DEMO_NICE]: {
        kind: "rv",
        config: {
            icon: "task-call-outgoing-new",
            variant: RVIconVariantOptions.DEFAULT,
            size: RVIconSizeOptions.LARGE,
        },
    },
    [NodeType.DEMO_Five9]: {
        kind: "rv",
        config: {
            icon: "task-call-outgoing-new",
            variant: RVIconVariantOptions.DEFAULT,
            size: RVIconSizeOptions.LARGE,
        },
    },
    [NodeType.StickyNote]: {
        kind: "rv",
        config: { icon: "sticky-note", color: "purple-8", variant: RVIconVariantOptions.DEFAULT },
    },
};

function renderRvIcon({ icon, size, variant, color }: RvIconConfig): JSX.Element {
    return (
        <RVIcon
            icon={icon}
            variant={variant ?? RV_ICON_DEFAULTS.variant}
            size={size ?? RV_ICON_DEFAULTS.size}
            color={color}
        />
    );
}

/** Journey node icon for builder blocks and read-only Copilot preview. */
export function getJourneyNodeIcon(nodeType: NodeType | undefined): JSX.Element {
    if (!nodeType) {
        return renderRvIcon({ icon: "conditional" });
    }

    const icon = NODE_TYPE_ICONS[nodeType];
    return icon.kind === "ant" ? icon.element : renderRvIcon(icon.config);
}
